/* eslint-disable consistent-return */
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router';

import { useAuth } from 'hooks/useAuth';

import { valueIsPresent } from 'helpers/format.cuboh';
import { buildMultiSelectOptions, buildSelectOptionsWithoutMerchants } from 'helpers/select.cuboh';
import { removeCubohPrefix } from 'helpers/company';

import {
  REPORT_PATHS,
  HIDE_MERCHANT_PATHS,
  START_DAY,
  END_DAY,
  ALL_LOCATIONS,
  findTimezone,
  SHOW_TIMEZONE_REGEX,
  FORCE_SELECT_MERCHANT_PATHS,
  HIDE_MERCHANT_INTEGRATION_TABS,
  HIDE_MULTI_LOCATION_SELECT_REGEX,
} from 'constants/cubohActionBar';
import { PLATFORMS } from 'constants/integrations';
import { getCustomDateFromParams } from 'containers/Reporting/helpers';
import { DATE_YMD, FULL_DATE_12_HOUR_FORMAT } from 'constants/dateTime';
import { CUBOH_COMPANY_PREFIX, QUERY_PARAMS } from 'constants/path';

export default function useProvideCubohActionBar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { companies, merchants, cubohUser, getMerchantIDsForCompany, isUserCubohOnly } = useAuth();
  const { restaurantId, action } = useParams();
  const { pathname } = useLocation();

  const [selectedTimezone, setSelectedTimezone] = useState('US/Eastern');
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const dateRange = getCustomDateFromParams(searchParams);

    if (dateRange && dateRange.length === 2) {
      return {
        start: dateRange[0].startOf('day').format(FULL_DATE_12_HOUR_FORMAT),
        end: dateRange[1].endOf('day').format(FULL_DATE_12_HOUR_FORMAT),
      };
    }

    return {
      start: START_DAY,
      end: END_DAY,
    };
  });

  const [actionBarSingleSelectedOption, setActionBarSingleSelectedOption] = useState();
  const [actionBarMultiSelectedOptions, setActionBarMultiSelectedOptions] = useState();
  const [selectedIntegrationTab, setSelectedIntegrationTab] = useState(PLATFORMS);

  const isMultiSelect = REPORT_PATHS.includes(pathname);
  const isMerchantSelect = !HIDE_MERCHANT_PATHS.includes(pathname);

  const pathNameMatches = FORCE_SELECT_MERCHANT_PATHS.some(match => pathname.match(match));
  const forceSelectOfMerchant =
    pathNameMatches && !HIDE_MERCHANT_INTEGRATION_TABS.includes(selectedIntegrationTab);
  const forceSelectOfCompany =
    HIDE_MERCHANT_PATHS.includes(pathname) ||
    HIDE_MERCHANT_INTEGRATION_TABS.includes(selectedIntegrationTab);

  const selectOptions = (() => {
    if (!cubohUser?.id || !companies || !merchants) {
      return;
    }

    if (!isMerchantSelect) {
      return buildSelectOptionsWithoutMerchants(companies);
    }
    return buildMultiSelectOptions(companies, merchants, cubohUser.id, isMultiSelect);
  })();

  // on first page load preselect the first company
  useEffect(() => {
    const companyIDs = Object.keys(companies);
    const isComingFromIntegrationsRedirect = !restaurantId && action;
    const hasCubohPrefix = restaurantId?.includes(CUBOH_COMPANY_PREFIX);
    if (
      !companyIDs.length ||
      isComingFromIntegrationsRedirect ||
      (isUserCubohOnly && restaurantId && !hasCubohPrefix)
    ) {
      return;
    }

    let selectedCompanyId;

    if (restaurantId) {
      // if the cuboh company prefix is in the URL, attempt to resolve the cuboh location ID
      if (hasCubohPrefix) {
        selectedCompanyId = removeCubohPrefix(restaurantId);
      } else {
        // premier locations will need to map via the chownow_restaurant_id param
        selectedCompanyId = Object.entries(companies).find(
          ([, company]) => company.chownow_restaurant_id === restaurantId,
        )?.[0];
      }
    }

    const firstCompany = companies[companyIDs[0]];
    const selectedCompany = companies[selectedCompanyId] || firstCompany;

    selectedCompanyId = selectedCompanyId || firstCompany.id;

    const companySelection = JSON.stringify({ companyID: selectedCompanyId });
    const validTimezone = findTimezone(selectedCompany.companyTimezone)?.value;

    if (validTimezone) {
      setSelectedTimezone(validTimezone);
    }

    if (!actionBarMultiSelectedOptions) {
      setActionBarMultiSelectedOptions([companySelection]);
    }

    // keep existing merchant selection if it's valid for the selected company
    // to prevent overriding merchant selection thats set by useIntegrationsRedirect
    if (actionBarSingleSelectedOption && forceSelectOfMerchant) {
      const parsedValues = JSON.parse(actionBarSingleSelectedOption);
      const existingCompanyId = parsedValues?.companyID;
      const existingMerchantId = parsedValues?.merchantID;
      const isSameCompany = Number(existingCompanyId) === Number(selectedCompanyId);

      if (isSameCompany && existingMerchantId) {
        // keep the existing merchant selection
        return;
      }
    }

    if (forceSelectOfMerchant) {
      const merchantID = getMerchantIDsForCompany(selectedCompanyId)[0];
      setActionBarSingleSelectedOption(
        JSON.stringify({
          companyID: Number(selectedCompanyId),
          merchantID: Number(merchantID),
        }),
      );
    } else {
      setActionBarSingleSelectedOption(companySelection);
    }
  }, [
    companies,
    restaurantId,
    actionBarMultiSelectedOptions,
    forceSelectOfMerchant,
    getMerchantIDsForCompany,
  ]);

  /* For single select pages we need to ensure that as the user navigates
   * we auto select just the company or the merchant (based on path)
   * eg. moving from integrations page (where the user is forced to click a merchant)
   * to the menu mapping page where the user is forced to not have a merchant selected
   */
  useEffect(() => {
    if (!actionBarSingleSelectedOption) return;

    const parsedValues = JSON.parse(actionBarSingleSelectedOption);

    if (forceSelectOfCompany && parsedValues.merchantID) {
      // moving from selected merchant to a company only page
      setActionBarSingleSelectedOption(
        JSON.stringify({
          companyID: Number(parsedValues?.companyID),
        }),
      );
    } else if (forceSelectOfMerchant && !parsedValues.merchantID) {
      // moving from a company only selection to a merchant required
      const options = getMerchantIDsForCompany(parsedValues?.companyID);
      const merchantID = options.length > 0 ? options[0] : 0;
      setActionBarSingleSelectedOption(
        JSON.stringify({
          companyID: Number(parsedValues?.companyID),
          merchantID: Number(merchantID),
        }),
      );
    } else if (!forceSelectOfMerchant && parsedValues.merchantID) {
      // unselect merchant on navigation
      setActionBarSingleSelectedOption(
        JSON.stringify({
          companyID: Number(parsedValues?.companyID),
        }),
      );
    }
  }, [forceSelectOfMerchant, forceSelectOfCompany, pathname]);

  // company id selected in the single select
  const singleSelectedCompanyID = (() => {
    if (!actionBarSingleSelectedOption) return null;
    return JSON.parse(actionBarSingleSelectedOption).companyID;
  })();

  const singleSelectedCompany = companies[singleSelectedCompanyID];

  // the merchants associated with the singleSelectedCompanyID (the single select)
  const merchantsForSelectedCompany = Object.values(merchants).filter(
    merchant => Number(merchant.companyID) === Number(singleSelectedCompanyID),
  );
  // the merchant ids associated with the singleSelectedCompanyID (the single select)
  const merchantIDsForSelectedCompany = getMerchantIDsForCompany(singleSelectedCompanyID);

  // merchant id selected in the single select
  // can be null if no merchant is selected (user selected the company only)
  // should not be null if a company only has one merchant
  const singleSelectedMerchantID = (() => {
    if (singleSelectedCompanyID) {
      const companyMerchants = Object.values(merchants).filter(
        ({ companyID }) => Number(companyID) === Number(singleSelectedCompanyID),
      );
      // if there is only one merchant for the company, it is the selected merchant
      if (companyMerchants.length === 1) {
        return JSON.parse(companyMerchants[0].id);
      }
    }
    if (!actionBarSingleSelectedOption) return null;
    return JSON.parse(actionBarSingleSelectedOption).merchantID;
  })();

  const singleSelectedMerchant = merchants[singleSelectedMerchantID];

  // list of company ids selected in the multi select
  const multiSelectedCompanyIDs = (() => {
    if (!actionBarMultiSelectedOptions?.length) return [];

    const parsedOptions = actionBarMultiSelectedOptions.map(option => JSON.parse(option));

    if (parsedOptions[0].companyID === ALL_LOCATIONS) {
      return Object.keys(companies);
    }

    return parsedOptions
      .filter(
        option =>
          valueIsPresent(companies[option.companyID]) &&
          Number(option.companyID) === Number(companies[option.companyID].id),
      )
      .map(option => option.companyID);
  })();

  // list of merchant ids selected in the multi select
  const multiSelectedMerchantIDs = (() => {
    if (!actionBarMultiSelectedOptions?.length) return [];

    const parsedOptions = actionBarMultiSelectedOptions.map(option => JSON.parse(option));

    if (parsedOptions[0].companyID === ALL_LOCATIONS) {
      return Object.keys(merchants);
    }

    const merchantIDs = [];
    parsedOptions.forEach(option => {
      if (!option.merchantID) {
        // the user has selected the company only, we need all of it's merchants
        merchantIDs.push(...getMerchantIDsForCompany(option.companyID));
      } else if (
        valueIsPresent(merchants[option.merchantID]) &&
        Number(option.merchantID) === Number(merchants[option.merchantID].id)
      ) {
        // the user has specifically added this merchant
        merchantIDs.push(option.merchantID);
      }
    });
    return merchantIDs;
  })();

  const showTimezoneSelect = SHOW_TIMEZONE_REGEX.test(pathname);
  const companyHasMultipleMerchants = merchantIDsForSelectedCompany.length > 1;
  const hideMultiLocationSelectFromPathname = HIDE_MULTI_LOCATION_SELECT_REGEX.test(pathname);

  const showMultiLocationSelect =
    !hideMultiLocationSelectFromPathname &&
    (Object.keys(companies).length > 1 || (isMerchantSelect && companyHasMultipleMerchants));

  // setSelectedDateRange but also persist the range to query params
  const wrappedSelectedDateRange = dateRange => {
    setSelectedDateRange(dateRange);
    setSearchParams(params => {
      const d0 = dayjs(dateRange.start).format(DATE_YMD);
      const d1 = dayjs(dateRange.end).format(DATE_YMD);
      params.set(QUERY_PARAMS.time, `${d0}-${d1}`);
      return params;
    });
  };

  return {
    selectedTimezone,
    setSelectedTimezone,
    selectOptions,
    actionBarSingleSelectedOption,
    setActionBarSingleSelectedOption,
    actionBarMultiSelectedOptions,
    setActionBarMultiSelectedOptions,
    isMultiSelect,
    isMerchantSelect,
    merchantsForSelectedCompany,
    merchantIDsForSelectedCompany,
    singleSelectedCompanyID,
    singleSelectedCompany,
    singleSelectedMerchantID,
    singleSelectedMerchant,
    multiSelectedMerchantIDs,
    multiSelectedCompanyIDs,
    selectedDateRange,
    setSelectedDateRange: wrappedSelectedDateRange,
    showTimezoneSelect,
    showMultiLocationSelect,
    selectedIntegrationTab,
    setSelectedIntegrationTab,
  };
}
